import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token on app initialization
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('lms_token');
      const userData = localStorage.getItem('lms_user');
      
      if (!token || !userData) {
        setLoading(false);
        return;
      }

      try {
        // Verify token with backend
        const response = await axios.get(`${API}/auth/verify-token`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Token is valid, set user data from backend response
        // This ensures we have the latest user data
        setUser(response.data);
        
        // Update localStorage with fresh user data
        localStorage.setItem('lms_user', JSON.stringify(response.data));
        
      } catch (error) {
        console.error('Token validation failed:', error.response?.data?.detail || error.message);
        
        // Token is invalid/expired - clear storage
        localStorage.removeItem('lms_token');
        localStorage.removeItem('lms_user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('lms_token', token);
    localStorage.setItem('lms_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('lms_token');
    localStorage.removeItem('lms_user');
    setUser(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('lms_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    login,
    logout,
    loading,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};